import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import {
  calculatePercentage,
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
} from "utils/utils";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";

const SOFTWARE_PROGRAM = "z20IsSBulcq";
const SOFTWARE_SYSTEM_TYPE = "Street Naming";
const SYSTEM_TYPE_DISPLAY_NAMES = ["System Type", "DPAT | System Type", "TEI | System Type"];
const REPORT_DATA_ELEMENT = "hM6AUNKRbKB";
const TRACKER_ENTITIES_PAGE_SIZE = 5000;
const TRACKER_EVENTS_PAGE_SIZE = 10000;
const TRACKER_ENTITY_FIELDS =
  "trackedEntity,attributes[attribute,displayName,value],enrollments[program,attributes[attribute,displayName,value]]";

function normalizeAttributeLabel(label) {
  return String(label || "")
    .toLowerCase()
    .replace(/^\s*(dpat|tei)\s*\|\s*/i, "")
    .trim();
}

function normalizeSystemTypeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function matchesStreetNamingSystemType(value) {
  const normalized = normalizeSystemTypeValue(value);
  if (!normalized) {
    return false;
  }

  return (
    normalized === "street naming"
    || normalized.includes("street naming")
    || normalized.replace(/\s/g, "") === "streetnaming"
  );
}

function collectSoftwareAttributes(instance) {
  const attributes = [...(instance?.attributes || [])];

  (instance?.enrollments || []).forEach((enrollment) => {
    if (!enrollment?.program || enrollment.program === SOFTWARE_PROGRAM) {
      attributes.push(...(enrollment?.attributes || []));
    }
  });

  return attributes;
}

function isSystemTypeAttribute(attribute) {
  if (!attribute) {
    return false;
  }

  const label = normalizeAttributeLabel(attribute.displayName);
  if (label === "system type") {
    return true;
  }

  return /system\s*type/i.test(String(attribute.displayName || ""));
}

function getSystemTypeValue(instance) {
  const attributes = collectSoftwareAttributes(instance);

  for (const name of SYSTEM_TYPE_DISPLAY_NAMES) {
    const value = getAttributeValue(name, { attributes });
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matchedAttribute = attributes.find(isSystemTypeAttribute);
  return matchedAttribute?.value || "";
}

function filterStreetNamingSoftware(instances = []) {
  const displayNameMatches = SYSTEM_TYPE_DISPLAY_NAMES.flatMap((name) =>
    formatDataGeneral(instances, name, SOFTWARE_SYSTEM_TYPE) || [],
  );

  if (displayNameMatches.length) {
    const uniqueMatches = new Map(
      displayNameMatches.map((instance) => [instance.trackedEntity, instance]),
    );
    return [...uniqueMatches.values()];
  }

  return instances.filter((instance) => matchesStreetNamingSystemType(getSystemTypeValue(instance)));
}

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function getSoftwareAttribute(unit, displayNames, attributeId) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, unit);
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matched = unit?.attributes?.find((attr) => attr.attribute === attributeId);
  return matched?.value || "N/A";
}

function buildInstallationRows(counterRows = []) {
  const counter = counterRows[0];
  if (!counter) {
    return [];
  }

  const totalStreets = Number(counter.street) || 0;
  const streetsNamed = Number(counter.streetNamedNo) || 0;
  const streetsInstalled = Number(counter.streetNamedInstalled) || 0;

  return [{
    ...counter,
    namedPercentage: totalStreets
      ? Number(calculatePercentage(streetsNamed, totalStreets).toFixed(2))
      : 0,
    installedPercentage: totalStreets
      ? Number(calculatePercentage(streetsInstalled, totalStreets).toFixed(2))
      : 0,
  }];
}

const StreetNaming = forwardRef(({
  year,
  streets,
  districtId,
  columns,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Title } = Typography;
  const [maxScore] = useState(3);
  const [softwareData, setSoftwareData] = useState([]);

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      streets: savedAssessmentData.streets ?? streets,
      software: savedAssessmentData.software ?? [],
    };
  }, [isAssessmentStarted, savedAssessmentData, streets]);

  const resolvedStreets = resolved?.streets ?? streets;
  const displaySoftware = resolved?.software ?? softwareData;
  const installationRows = useMemo(
    () => buildInstallationRows(resolvedStreets?.counter),
    [resolvedStreets?.counter],
  );

  const scorei = resolvedStreets?.data?.length > 0 && resolvedStreets.data[0].street === "YES" ? 1 : 0;
  const scoreii = resolvedStreets?.percentage >= 70 ? 2 : 0;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI3",
      area: "Physical and Spatial Planning Services",
      maxScore,
      streets: resolvedStreets,
      software: displaySoftware,
      scorei,
      scoreii,
    }),
  }));

  const softwareColumns = [
    { title: "Software Name", dataIndex: "name", key: "name" },
    { title: "Purchase Date", dataIndex: "purchaseDate", key: "purchaseDate" },
    { title: "Cost", dataIndex: "cost", key: "cost" },
    {
      title: "Report",
      dataIndex: "report",
      key: "report",
      render: (value) => renderDpatEvidenceLink(value, "View Report"),
    },
  ];

  const installationColumns = [
    { title: "No. of streets available on database (a)", dataIndex: "street", key: "street" },
    { title: "Number of streets named (b)", dataIndex: "streetNamedNo", key: "streetNamedNo" },
    { title: "Number of street signage’s installed (c)", dataIndex: "streetNamedInstalled", key: "streetNamedInstalled" },
    {
      title: "% of streets named (b/a x 100)",
      dataIndex: "namedPercentage",
      key: "namedPercentage",
    },
    {
      title: "% of streets installed (c/a x 100)",
      dataIndex: "installedPercentage",
      key: "installedPercentage",
    },
  ];

  const loadSoftwareData = useCallback(() => {
    if (!districtId || year == null) {
      setSoftwareData([]);
      return;
    }

    axios
      .get(
        `/tracker/trackedEntities?orgUnit=${districtId}&program=${SOFTWARE_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=${TRACKER_ENTITIES_PAGE_SIZE}&fields=${TRACKER_ENTITY_FIELDS}`,
      )
      .then((result) => {
        const instances = filterStreetNamingSoftware(result.data?.instances || []);
        if (!instances.length) {
          setSoftwareData([]);
          return;
        }

        axios
          .get(
            `/tracker/events?program=${SOFTWARE_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=${TRACKER_EVENTS_PAGE_SIZE}`,
          )
          .then((resp) => {
            const reports = resp.data?.instances || [];
            const temp = instances.map((system, index) => {
              const reportLink = getFileLinkIfExist(
                reports,
                REPORT_DATA_ELEMENT,
                system.trackedEntity,
              );

              return {
                key: system.trackedEntity || index + 1,
                name: getSoftwareAttribute(system, ["Name"], "wca7mlI2exE"),
                purchaseDate: getSoftwareAttribute(
                  system,
                  ["Purchase Date"],
                  "YvIn2QEep25",
                ),
                cost: getSoftwareAttribute(
                  system,
                  ["Purchase Cost", "Purchased cost"],
                  "J2GkQ7g6IFk",
                ),
                report: reportLink
                  ? fileUrl(reportLink, REPORT_DATA_ELEMENT)
                  : "Not Uploaded",
              };
            });

            setSoftwareData(temp);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: loadSoftwareData,
    applySnapshot: (snap) => {
      if (snap.software) setSoftwareData(snap.software);
    },
    deps: [districtId, year, loadSoftwareData],
  });

  const DPAT_SLUG = "sdi3.0-3.3";

  return (
    <Comment
      data={resolvedStreets?.data}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi3.0-3.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.3i Actual Score" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.3ii Actual Score" score={scoreii} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "20px" }}>Evidence of Street Naming Database</Title>
          <Table
            columns={columns}
            dataSource={resolvedStreets?.data || []}
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "30px" }}>Evidence of Street Naming Software</Title>
          <Table
            columns={softwareColumns}
            dataSource={displaySoftware}
            rowKey="key"
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "30px" }}>Evidence of Installation of named streets</Title>
          <Table
            columns={installationColumns}
            dataSource={installationRows}
            rowKey="street"
            pagination={false}
            bordered
          />

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default StreetNaming;
